import { supabase } from '../lib/supabase';

/**
 * Service for managing professional registration Step 2 data
 */
export const professionalRegistrationService = {
  /**
   * Save all Step 2 data (work areas, VAT, documents, extended availability)
   * @param {Object} data - Step 2 form data
   * @param {string} userId - User ID from auth.uid()
   * @returns {Promise<{success: boolean, error?: string}>}
   */
  async saveStep2Data(data, userId) {
    try {
      if (!userId) {
        throw new Error('User ID is required');
      }

      // 1. Save work areas
      if (data?.primaryWorkAreas && data?.primaryWorkAreas?.length > 0) {
        // Delete existing work areas for this user
        await supabase
          ?.from('professional_work_areas')
          ?.delete()
          ?.eq('user_id', userId);

        // Insert new work areas
        const workAreasToInsert = data?.primaryWorkAreas?.map(area => ({
          user_id: userId,
          mapbox_id: area?.mapbox_id || area?.id,
          name: area?.name,
          full_name: area?.fullName || area?.full_address || area?.name,
          details: area?.details || {},
          is_primary: true
        }));

        const { error: workAreasError } = await supabase
          ?.from('professional_work_areas')
          ?.insert(workAreasToInsert);

        if (workAreasError) {
          throw new Error(`Failed to save work areas: ${workAreasError?.message}`);
        }
      }

      // 2. Save extended availability
      if (data?.extendedAvailability) {
        const { error: availabilityError } = await supabase
          ?.from('professional_extended_availability')
          ?.upsert({
            user_id: userId,
            enabled: data?.extendedAvailability?.enabled || false,
            all_italy: data?.extendedAvailability?.allItaly || false,
            include_sardinia_sicily: data?.extendedAvailability?.includeSardiniaSicily || false
          }, {
            onConflict: 'user_id'
          });

        if (availabilityError) {
          throw new Error(`Failed to save extended availability: ${availabilityError?.message}`);
        }
      }

      // 3. Save VAT number
      if (data?.vatNumber) {
        const { error: vatError } = await supabase
          ?.from('professional_vat_info')
          ?.upsert({
            user_id: userId,
            vat_number: data?.vatNumber,
            validated_at: new Date()?.toISOString()
          }, {
            onConflict: 'user_id'
          });

        if (vatError) {
          throw new Error(`Failed to save VAT number: ${vatError?.message}`);
        }
      }

      // 4. Save document metadata
      if (data?.documentFile && data?.documentType) {
        const { error: documentError } = await supabase
          ?.from('professional_documents')
          ?.insert({
            user_id: userId,
            document_type: data?.documentType,
            file_name: data?.documentFile?.name,
            storage_path: data?.documentFile?.storagePath,
            original_size: data?.documentFile?.originalSize,
            optimized_size: data?.documentFile?.optimizedSize,
            mime_type: data?.documentFile?.type
          });

        if (documentError) {
          throw new Error(`Failed to save document metadata: ${documentError?.message}`);
        }
      }

      // 5. Update user registration_step to 2 (not completing onboarding yet)
      const { error: userUpdateError } = await supabase
        ?.from('users')
        ?.update({ registration_step: 2 })
        ?.eq('id', userId);

      if (userUpdateError) {
        throw new Error(`Failed to update registration step: ${userUpdateError?.message}`);
      }

      return { success: true };
    } catch (error) {
      console.error('Error saving Step 2 data:', error);
      return {
        success: false,
        error: error?.message || 'Si è verificato un errore durante il salvataggio dei dati'
      };
    }
  },

  /**
   * Validate if all Step 2 sections are completed
   * @param {Object} formData - Current form data
   * @returns {boolean}
   */
  validateStep2Completion(formData) {
    // Check work areas (at least 1 area selected)
    const hasWorkAreas = formData?.primaryWorkAreas && formData?.primaryWorkAreas?.length > 0;

    // Check VAT number (must be exactly 11 digits)
    const hasValidVat = formData?.vatNumber && formData?.vatNumber?.length === 11;

    // Check document (must have type and file uploaded)
    const hasDocument = formData?.documentType && formData?.documentFile && formData?.documentFile?.storagePath;

    return hasWorkAreas && hasValidVat && hasDocument;
  },

  /**
   * Save payment authorization status
   * @param {string} userId - User ID from auth.uid()
   * @param {string} paymentMethodId - Stripe payment method ID
   * @returns {Promise<{success: boolean, error?: string}>}
   */
  async savePaymentAuthorization(userId, paymentMethodId) {
    try {
      if (!userId) {
        throw new Error('User ID is required');
      }

      // Update user payment_authorized status and registration_step to 3
      const { error: userUpdateError } = await supabase
        ?.from('users')
        ?.update({ 
          payment_authorized: true,
          registration_step: 3
        })
        ?.eq('id', userId);

      if (userUpdateError) {
        throw new Error(`Failed to update payment authorization: ${userUpdateError?.message}`);
      }

      return { success: true };
    } catch (error) {
      console.error('Error saving payment authorization:', error);
      return {
        success: false,
        error: error?.message || 'Si è verificato un errore durante il salvataggio'
      };
    }
  }
};

export default professionalRegistrationService;